/**
 * Trusted-device storage. Only used when the person chose "My own trusted device" in Privacy &
 * storage. Keeps a bounded, per-user copy of work that has not reached the server yet (unsaved
 * drafts, unsent chat messages) so it survives closing the tab while offline. It is never the
 * only home of acknowledged work, and everything is cleared on sign-out.
 *
 * Browser storage can be unavailable, limited or evicted (private browsing, quota); failures are
 * reported to the caller and never block the app.
 */

const DB_NAME = 'deskworks-trusted';
const STORE = 'items';
const MAX_ITEMS_PER_USER = 200;

export interface TrustedItem<T = unknown> {
  key: string;
  userId: string;
  kind: 'draft' | 'outbox';
  data: T;
  updatedAt: number;
}

let dbPromise: Promise<IDBDatabase> | null = null;

function open(): Promise<IDBDatabase> {
  if (typeof indexedDB === 'undefined') return Promise.reject(new Error('unavailable'));
  dbPromise ??= new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, 1);
    req.onupgradeneeded = () => {
      const store = req.result.createObjectStore(STORE, { keyPath: 'key' });
      store.createIndex('user', 'userId');
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => {
      dbPromise = null;
      reject(req.error ?? new Error('unavailable'));
    };
  });
  return dbPromise;
}

function tx<T>(mode: IDBTransactionMode, fn: (store: IDBObjectStore) => IDBRequest<T> | void): Promise<T | undefined> {
  return open().then(
    (db) =>
      new Promise<T | undefined>((resolve, reject) => {
        const t = db.transaction(STORE, mode);
        const req = fn(t.objectStore(STORE));
        t.oncomplete = () => resolve(req ? (req.result as T) : undefined);
        t.onerror = () => reject(t.error ?? new Error('storage error'));
        t.onabort = () => reject(t.error ?? new Error('storage aborted'));
      }),
  );
}

export async function putTrusted<T>(userId: string, kind: TrustedItem['kind'], id: string, data: T): Promise<void> {
  const item: TrustedItem<T> = { key: `${userId}:${kind}:${id}`, userId, kind, data, updatedAt: Date.now() };
  await tx('readwrite', (s) => s.put(item));
  // Keep the per-user footprint bounded (oldest first).
  const all = await listTrusted(userId);
  if (all.length > MAX_ITEMS_PER_USER) {
    const extra = all.sort((a, b) => a.updatedAt - b.updatedAt).slice(0, all.length - MAX_ITEMS_PER_USER);
    await tx('readwrite', (s) => {
      for (const e of extra) s.delete(e.key);
    });
  }
}

export async function deleteTrusted(userId: string, kind: TrustedItem['kind'], id: string): Promise<void> {
  await tx('readwrite', (s) => s.delete(`${userId}:${kind}:${id}`));
}

export async function listTrusted<T = unknown>(userId: string, kind?: TrustedItem['kind']): Promise<TrustedItem<T>[]> {
  const rows = ((await tx('readonly', (s) => s.index('user').getAll(userId))) ?? []) as TrustedItem<T>[];
  return kind ? rows.filter((r) => r.kind === kind) : rows;
}

/** Removes every trusted-device item (sign-out, account switch). */
export async function clearTrusted(): Promise<void> {
  try {
    await tx('readwrite', (s) => s.clear());
  } catch {
    // Nothing stored or storage unavailable.
  }
}
